import { Add, Delete, Edit, FilterList, RemoveRedEye } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import {
  IWorkshop,
  IWorkshopCancellationReasons,
  IWorkshopInput,
  WorkshopStatus,
} from "models/workshop";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import BlockIcon from "@mui/icons-material/Block";
import { getCompanyProficiencyLevels } from "framework/companyProficiencyLevel";
import { getCompetencies } from "framework/competencies";
import { getTrainers } from "framework/trainer";
import {
  WorkshopCells,
  WorkshopCellsExport,
  addWorkshop,
  deleteWorkshop,
  editWorkshop,
  formatExcellValue,
  getCancellationReasons,
  getWorkshops
} from "framework/workshops";
import { getPermission, getUserData } from "helpers";
import { formatToday, handleError } from "helpers/utils";
import useFetchSection from "hooks/useFetchSection";
import { ICompetency } from "models/competency";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { UserRoleName, UserType } from "types/auth";
import AddWorkshop from "./partials/AddWorkshop/AddWorkshop";
import DeleteWorkshop from "./partials/DeleteWorkshop/DeleteWorkshop";
import EditWorkshop from "./partials/EditWorkshop/EditWorkshop";
import FilterListModal from "./partials/FilterListModal";
import dayjs from "dayjs";
import { getCompanies, getCompanyTimeZone } from "framework/company";
import { ICompany } from "models/company";


interface Props {
  feature: Feature;
}

interface _Filter {
  vendors?: any;
  venues?: any;
  modalities?: any;
  trainers?: any;
  topics?: any;
  cities?: any;
  companies?: any;
  countries?: any;
  competencies?: any;
  profLevel?: any;
  cancellationReasons?: IWorkshopCancellationReasons[];
  languages?: any
}

const CorporateWorkshops: React.FC<Props> = ({ feature }) => {
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterWorkshops, setFilterWorkshops] = useState<IWorkshop[]>(
    [] as IWorkshop[]
  );
  const [Competencies, setCompetencies] = useState<ICompetency[]>(
    [] as ICompetency[]
  );
  const [filters, setFilters] = useState<_Filter>({});
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);

  const [selectedWorkshop, setSelectedWorkshop] = useState<IWorkshop>(
    {} as IWorkshop
  );
  const [companyTimeZone, setCompanyTimeZone] = useState<string>("");

  const filtersRef = useRef<any>(null);
  const userData = getUserData();
  const { colors, user, roleName } = userData;
  const userType = user.userType;
  const [inputObj, setInputObj] = useState<IWorkshopInput>(
    {} as IWorkshopInput
  );
  const { setAlert } = useAlert();

  const now = new Date();
  const year = now.getFullYear();


  const [openFilter, setOpenFilter] = useState(false);
  const [filterState, setFilterState] = useState({
    department: "",
    provider: "",
    vendors: "",
    venues: "",
    topic: "",
    modalities: "",
    countries: "",
    cities: "",
    languages: "",
    trainers: "",
    startDate: new Date(year, 0, 1),
    endDate: new Date(year, 11, 31),
  });
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addWorkshopsMutation = useMutation({
    mutationFn: (createInput: IWorkshopInput) => {
      return addWorkshop({
        ...createInput,
        capacity: +createInput.capacity,
        status: WorkshopStatus.ACTIVE,
      });
    },
  });

  const editWorkshopsMutation = useMutation({
    mutationFn: (data: { createInput: IWorkshopInput; id: string }) => {
      return editWorkshop(data.createInput, selectedWorkshop.workshopId);
    },
  });

  const deleteWorkshopsMutation = useMutation({
    mutationFn: (code: string) => {
      return deleteWorkshop(code);
    },
  });

  const {
    loading,
    data: Workshops,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getWorkshops(),
    fetchPermission: () => getPermission(6),
    section: "Workshops",
    onSuccess: (data) => {
      setFilterWorkshops(data)
    },
    processResponse: (data) =>
      data.map((item) => {
        return { ...item, requiresInvitationStr: item.requiresInvitation ? 'Yes' : 'No', };
      }),
  });

  // get filters
  const fetchFilters = async () => {
    try {
      const [
        trainersResponse,
        competenciesResponse,
        companiesResponse,
        profLevelResponse,
        cancellationReasonsResponse,
        filtersResponse,
      ] = await Promise.all([

        queryClient.fetchQuery("get Trainers", () => getTrainers()),
        queryClient.fetchQuery("get Competencies", () => getCompetencies()),
        queryClient.fetchQuery("get Companies", () => getCompanies()),
        queryClient.fetchQuery("get Prof level", () =>
          getCompanyProficiencyLevels()
        ),
        queryClient.fetchQuery("get Cancellation Reasons", () =>
          getCancellationReasons()
        ),
        queryClient.fetchQuery("getWorkshopFilters", () => getWorkshops(`/filters`))
      ]);

      setFilters({
        trainers: trainersResponse,
        competencies: competenciesResponse,
        companies: companiesResponse,
        profLevel: profLevelResponse,
        cancellationReasons: cancellationReasonsResponse,
        ...filtersResponse
      });
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    }
  };

  const fetchCompanyTimeZone = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get company timezone", () =>
        getCompanyTimeZone()
      );
      setCompanyTimeZone(response);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchFilters();
    fetchCompanyTimeZone();
    // eslint-disable-next-line
  }, []);

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {

    if (name === "companies") {
      let companies = value.map((company: any) => company.value);
      setInputObj((prev) => ({
        ...prev,
        [name]: companies,
      }));
      return
    }

    if (name === "competencies") {
      let competencies = value
        .map((competency: any) => competency.value)
        .join(";");
      setInputObj((prev) => ({
        ...prev,
        [name]: competencies,
      }));
      return
    }

    if (name.includes('Time')) {
      const date = dayjs(value);
      const time = `${String(date.hour()).padStart(2, '0')}:${String(date.minute()).padStart(2, '0')}:00`;
      setInputObj((prev) => ({
        ...prev,
        [name]: time,
      }));
      return
    }

    if (name === "languages") {
      let languages = value
        .map((language: any) => ({ Id: language.value }))

      setInputObj((prev) => ({
        ...prev,
        [name]: languages,
      }));
      return
    }
    if (name === "requires_invitation") {
      setInputObj((prev) => ({
        ...prev,
        [name]: value || false,
      }));
      return
    }


    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));

  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Workshop: IWorkshop) => {
    setSelectedWorkshop(Workshop);
    setInputObj({modality: Workshop.modality} as IWorkshopInput)
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedWorkshop({} as IWorkshop);
  };

  const handleOpenDelete = (Workshop: IWorkshop) => {
    setSelectedWorkshop(Workshop);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedWorkshop({} as IWorkshop);
  };

  const getBodyCells = (row: IWorkshop) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "companyName")
        return {
          id: "companyName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.companyName : row.companyNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "vendorName")
        return {
          id: "vendorName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.vendorName : row.vendorNameAr}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "venueName")
        return {
          id: "venueName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.venueName : row.venueNameAr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "trainer")
        return {
          id: "trainer",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.trainer : row.trainerNameAr}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "topicName")
        return {
          id: "topicName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row.topicName : row.topicNameAr}
              </Typography>
            </TableCell>
          ),
        };
      
      if (key === "capacity")
        return {
          id: "capacity",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.capacity}</Typography>
            </TableCell>
          ),
        };
      
      if (key === "roomNumber") return undefined;
      
      if (key === "languages")
        return {
          id: "languages",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.languages.map(a => a.name).join(',')}</Typography>
            </TableCell>
          ),
        };
      
      if (key === "numberOfEnrollments")
        return {
          id: "numberOfEnrollments",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.numberOfEnrollments || 0}</Typography>
            </TableCell>
          ),
        };
      
      if (key === "evaluationCount")
        return {
          id: "evaluationCount",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.evaluationCount || 0}</Typography>
            </TableCell>
          ),
        };
      
      if (key === "pretestCount")
        return {
          id: "pretestCount",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.pretestCount || 0}</Typography>
            </TableCell>
          ),
        };
      
      if (key === "posttestCount")
        return {
          id: "posttestCount",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.posttestCount || 0}</Typography>
            </TableCell>
          ),
        };
      
      if (key === "link")
        return {
          id: "link",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.link}</Typography>
            </TableCell>
          ),
        };

      if (key === "requiresInvitation")
        return {
          id: "requiresInvitation",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.requiresInvitationStr}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "prerequisite")
        return {
          id: "prerequisite",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.prerequisite}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "attendanceFrequency")
        return {
          id: "attendanceFrequency",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.attendanceFrequency}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "companies")
        return {
          id: "companies",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row!?.companies!?.map(a => filters?.companies?.find((item: ICompany) => item?.id === a)?.name).join(',')}</Typography>
            </TableCell>
          ),
        };

      if (key === "fromDate")
        return {
          id: "fromDate",
          element: (
            <TableCell align={"left"} key={i}>
              {row.fromDate && (
                <Typography variant="body2">
                  {new Date(row.fromDate).toLocaleDateString()}
                </Typography>
              )}
            </TableCell>
          ),
        };
      if (key === "toDate")
        return {
          id: "toDate",
          element: (
            <TableCell align={"left"} key={i}>
              {row.toDate && (
                <Typography variant="body2">
                  {new Date(row.toDate).toLocaleDateString()}
                </Typography>
              )}
            </TableCell>
          ),
        };

      if (key === "fromTime") {
        const from = row.fromTimeDisplay || row.fromTime || "";
        const to = row.toTimeDisplay || row.toTime || "";
        const range = [from, to].filter(Boolean).join(" - " );

        return {
          id: "fromTime",
          element: (
            <TableCell align={"left"} key={i}>
              {range && (
                <Typography variant="body2">
                  {range}
                </Typography>
              )}
            </TableCell>
          ),
        };
      }
      if (key === "toTime") return undefined;

      if (key === "averagePreScore")
        return {
          id: "averagePreScore",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.averagePreScore.toString()}
              </Typography>
            </TableCell>
          ),
        };
      if (key === "coHost") return undefined;
      if (key === "averagePostScore")
        return {
          id: "averagePostScore",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.averagePostScore.toString()}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "workshopId")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {userType !== UserType.VENDOR_USER &&
                  permission?.hasDeletePermission && (
                    <Tooltip title={t("T_delete")}>
                      <IconButton
                        size={"small"}
                        sx={{
                          color: colors.deleteIconColor
                            ? colors.deleteIconColor
                            : "red",
                          "&:hover": { color: colors?.deleteIconHoverColor },
                        }}
                        onClick={() => handleOpenDelete(row)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  )}

                <Tooltip title={t("T_details")}>
                  <Link to={`${row.workshopId}`}>
                    <IconButton size={"small"} color={"info"}>
                      <RemoveRedEye
                        sx={{
                          color: colors.viewIconColor
                            ? colors.viewIconColor
                            : "#0288d1",
                          "&:hover": { color: colors?.viewIconHoverColor },
                        }}
                      />
                    </IconButton>
                  </Link>
                </Tooltip>
              </Stack>
            </TableCell>
          ),
        };
      else return <></>;
    });

    return cells;
  };


  const handleFilterChange = (key: string, value: string) => {
    setFilterState((prev) => ({ ...prev, [key]: value }));
    makeRequest({ ...filterState, [key]: value });
  };

  const makeRequest = (filterData: typeof filterState) => {
    let filtered = Workshops!;
    const { department, vendors, venues,trainers, provider, topic, modalities, countries, cities, languages } = filterData;

    if (provider) filtered = filtered.filter((w) => w.companyId === provider);
    if (topic) filtered = filtered.filter((w) => w.topicId === topic);
    if (trainers) filtered = filtered.filter((w) => w.trainerId === trainers);
    if (modalities) filtered = filtered.filter((w) => w.modality === +modalities);
    if (venues) filtered = filtered.filter((w) => w.venueId === venues);
    if (vendors) filtered = filtered.filter((w) => w.vendorId === vendors);
    if (countries) filtered = filtered.filter((w) => w.countryId === countries);
    if (cities) filtered = filtered.filter((w) => w.cityId === cities);
    if (languages) filtered = filtered.filter((w) => w.languages.find((l) => l.id === languages));
    if (filterData.startDate) filtered = filtered.filter((w) => new Date(w.fromDate) >= filterData.startDate);
    if (filterData.endDate) filtered = filtered.filter((w) => new Date(w.toDate) <= filterData.endDate);

    setFilterWorkshops(filtered);
  };




  const resetFilters = () => {
    if (filtersRef.current) {
      filtersRef.current.resetFilters(); // Call the reset function from Filters component
    }
    makeRequest({
      department: "",
      provider: "",
      vendors: "",
      venues: "",
      topic: "",
      modalities: "",
      countries: "",
      trainers: "",
      cities: "",
      languages: "",
      startDate: new Date(year, 0, 1),
      endDate: new Date(year, 11, 31),
    });
    setFilterState({
      department: "",
      provider: "",
      vendors: "",
      venues: "",
      topic: "",
      modalities: "",
      countries: "",
      trainers: "",
      cities: "",
      languages: "",
      startDate: new Date(year, 0, 1),
      endDate: new Date(year, 11, 31),
    })
  };

  const handleAdd = async () => {
    if (
      !inputObj.vendorId ||
      !inputObj.topicId ||
      // !inputObj.venueId ||
      !inputObj.modality ||
      !inputObj.languages ||
      !inputObj.capacity
    )
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });

    if (inputObj.modality === 2 && !inputObj.link)
      return setAlert({
        open: true,
        message: "Link is required",
        type: "error",
      });


     if (!inputObj.fromDate) inputObj.fromDate = formatToday();
    if (!inputObj.toDate) inputObj.toDate = formatToday();
    inputObj.islocked = inputObj.islocked ? true :false
    try {
      setLoading2(true);
      await addWorkshopsMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Workshop added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async (data: any[]) => {
    try {
      setLoading2(true);
      let createInput: IWorkshopInput = {
        vendorId: inputObj.vendorId || selectedWorkshop.vendorId,
        venueId: inputObj.venueId || selectedWorkshop.venueId,
        trainerId: inputObj.trainerId || selectedWorkshop.trainerId,
        companies: inputObj.companies,
        topicId: inputObj.topicId || selectedWorkshop.topicId,
        status: +inputObj.status || +selectedWorkshop.status,
        roomNumber: inputObj.roomNumber,
        capacity: inputObj.capacity || selectedWorkshop.capacity,
        fromDate: inputObj.fromDate || selectedWorkshop.fromDate,
        toDate: inputObj.toDate || selectedWorkshop.toDate,
        fromTime: inputObj.fromTime || selectedWorkshop.fromTime,
        toTime: inputObj.toTime || selectedWorkshop.toTime,
        link: inputObj.link || selectedWorkshop.link,
        attendanceFrequency: inputObj.attendanceFrequency || selectedWorkshop.attendanceFrequency,
        modality: +inputObj.modality || +selectedWorkshop.modality,
        prerequisite: inputObj.prerequisite ?? selectedWorkshop.prerequisite,
        requires_invitation: inputObj.requires_invitation ?? (selectedWorkshop.requiresInvitation || false),
        languages: inputObj.languages || selectedWorkshop.languages,
        topicCompetencies: data,
        islocked:inputObj.islocked || selectedWorkshop.isLocked,
      };
      await editWorkshopsMutation.mutateAsync({
        createInput,
        id: selectedWorkshop.workshopId,
      });
      setAlert({
        open: true,
        message: "Workshop updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteWorkshopsMutation.mutateAsync(selectedWorkshop.workshopId);
      setAlert({
        open: true,
        message: "Workshop deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };



  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            spacing={1}
            width={"100%"}
            marginBottom={5}
          >
            <Typography
              variant="h3"
              color={colors?.labelColor ? colors?.labelColor : "header.primary"}
            >
              {t(feature.name)}
            </Typography>
            <Stack direction={"row"} spacing={1}>
              {userType !== UserType.VENDOR_USER &&
                permission?.hasModifyPermission &&
                filters && (
                  <Tooltip title="Add Workshop">
                    <IconButton
                      sx={{
                        color: colors.addUpdateIconColor
                          ? colors.addUpdateIconColor
                          : "red",
                        "&:hover": { color: colors?.addUpdateIconHoverColor },
                        height: 40,
                        width: 40,
                        transition: "0.5s",
                      }}
                      onClick={handleOpenAdd}
                    >
                      <Add />
                    </IconButton>
                  </Tooltip>
                )}
              <Tooltip title={t('T_filter_workshops')}>
                <IconButton onClick={() => setOpenFilter(true)}>
                  <FilterList />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterWorkshops.length > 0 && (
          <List
            headCells={WorkshopCells}
            data={FilterWorkshops}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
            formatExcelValue={formatExcellValue}
            exportHeaders={WorkshopCellsExport}
          />
        )}
        {(!FilterWorkshops || FilterWorkshops.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_workshop_header")}
              message={t("T_no_workshop_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && filters && (
        <AddWorkshop
          open={openAdd}
          loading={loading2}
          vendors={filters.vendors}
          venues={filters.venues}
          countries={filters.countries}
          cities={filters.cities}
          trainers={filters.trainers}
          companies={filters.companies}
          topics={filters.topics}
          profLevel={filters.profLevel}
          modalities={filters.modalities}
          languages={filters.languages}
          competencies={filters.competencies}
          workshopEvaluations={[]}
          timeZone={companyTimeZone}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
          inputObj={inputObj}
        />
      )}
      {openEdit && filters && (
        <EditWorkshop
          open={openEdit}
          loading={loading2}
          vendors={filters.vendors}
          venues={filters.venues}
          countries={filters.countries}
          cities={filters.cities}
          trainers={filters.trainers}
          companies={filters.companies}
          topics={filters.topics}
          profLevel={filters.profLevel}
          modalities={filters.modalities}
          competencies={Competencies}
          workshop={selectedWorkshop}
          languages={filters.languages}
          workshopEvaluations={[]}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          inputObj={inputObj}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteWorkshop
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}

      {openFilter && (
        <FilterListModal
          open={openFilter}
          filters={filters}
          {...filterState}
          onClose={() => setOpenFilter(false)}
          onReset={resetFilters}
          onChangeCities={(e: any) => handleFilterChange("cities", e)}
          onChangeCountries={(e: any) => handleFilterChange("countries", e)}
          onChangeLanguages={(e: any) => handleFilterChange("languages", e)}
          onChangeModalities={(e: any) => handleFilterChange("modalities", e)}
          onChangeTopic={(e: any) => handleFilterChange("topic", e)}
          onChangeTrainer={(e: any) => handleFilterChange("trainers", e)}
          onChangeVenues={(e: any) => handleFilterChange("venues", e)}
          onChangeVendor={(e: any) => handleFilterChange("vendors", e)}
          onChangeStartDate={(e: any) => handleFilterChange("startDate", e)}
          onChangeEndDate={(e: any) => handleFilterChange("endDate", e)}
        />
      )}
    </>
  );
};

export default CorporateWorkshops;
